import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse, ApiListResponse } from '../lib/response';
import {
  Integration,
  IntegrationAuthenticate,
  IntegrationOption,
  IntegrationPaymentOrder,
} from '../models/integration';
import { CustomerCreditCard } from '../models/customerCreditCard';
import { Order } from '../models/order';

export class IntegrationService extends BaseService<Integration> {
  override model = Integration;

  override modelName = 'integration';
  override modelLabel = 'Integrations';

  async index(): Promise<ApiListResponse<Integration>> {
    const request = new ApiRequest({
      method: 'GET',
      url: this.modelBaseUrl,
    });

    return await this.requestList<Integration>(request, Integration);
  }

  async info(guid: string): Promise<ApiResponse<Integration>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/info`,
      query: { guid },
    });

    return await this.requestItem<Integration>(request, Integration);
  }

  async active(
    { modifiedSince }: { modifiedSince?: Date } = {}
  ): Promise<Record<string, any>> {
    const headers: Record<string, string> = {};
    if (modifiedSince) {
      headers['If-Modified-Since'] = modifiedSince.toUTCString();
    }

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/active`,
      headers,
    });

    const response = await this.request(request);
    return response.data ?? {};
  }

  async authenticate(
    guid: string,
    params: Record<string, string>
  ): Promise<ApiResponse<IntegrationAuthenticate>> {
    const query: Record<string, string> = { guid, ...params };

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/authenticate`,
      query,
    });

    return await this.requestItem<IntegrationAuthenticate>(request, IntegrationAuthenticate);
  }

  async unauthenticate(guid: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/unAuthenticate`,
      query: { guid },
    });

    return await this.request(request);
  }

  async getOptions(guid: string): Promise<Record<string, any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/options`,
      query: { guid },
    });

    const response = await this.request(request);
    return response.data;
  }

  async setOptions(
    guid: string,
    options: Record<string, any>
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = {};
    for (const key in options) {
      data[key] = options[key].currentValue ?? options[key];
    }

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/options`,
      query: { guid },
      data,
    });

    const response = await this.request(request);
    return response.data;
  }

  async runAction(
    guid: string,
    { action, async = false, data = null }: { action: string; async?: boolean; data?: any }
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/runAction`,
      query: { guid },
      data: { action, async, data },
    });

    return await this.request(request);
  }

  async integrationRequest(guid: string, action: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/request`,
      query: { guid, action },
    });

    return await this.request(request);
  }

  async terminal(
    guid: string,
    { action, params = {} }: { action: string; params?: Record<string, any> }
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/terminals`,
      query: { guid },
      data: { action, params },
    });

    return await this.request(request);
  }

  async paymentOrder(): Promise<ApiResponse<IntegrationPaymentOrder>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/reorderPayment`,
    });

    return await this.requestItem<IntegrationPaymentOrder>(request, IntegrationPaymentOrder);
  }

  async reorderPayment(order: string[]): Promise<ApiResponse<IntegrationPaymentOrder>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/reorderPayment`,
      data: { order },
    });

    return await this.requestItem<IntegrationPaymentOrder>(request, IntegrationPaymentOrder);
  }

  async processPayment(
    guid: string,
    {
      orderId,
      amount,
      params,
      idempotencyKey,
    }: {
      orderId: string;
      amount: number;
      params: Record<string, any>;
      idempotencyKey: string;
    }
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/processPayment`,
      data: {
        guid,
        order: orderId,
        amount,
        params,
        idempotency_key: idempotencyKey,
      },
    });

    return await this.request(request);
  }

  async processRefund(
    guid: string,
    { orderId, amount, params }: { orderId: string; amount: number; params: Record<string, any> }
  ): Promise<ApiResponse<Order>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/processRefund`,
      data: { guid, order: orderId, amount, params },
    });

    return await this.requestItem<Order>(request, Order);
  }

  async saveCardIntegration({
    guid,
    customerId,
    params,
  }: {
    guid: string;
    customerId: string;
    params: Record<string, any>;
  }): Promise<ApiResponse<CustomerCreditCard>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/saveCardIntegration`,
      data: {
        guid,
        idCustomer: customerId,
        paymentIntegrationParams: params,
      },
    });

    return await this.requestItem<CustomerCreditCard>(request, CustomerCreditCard);
  }
}
